import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { exerciseLibrary } from '../../services/api';
import { Chip, ConfirmDialog, errorList } from './shared';

/*
 * Validação da biblioteca: o que espera a decisão de uma pessoa, de TODOS os exercícios, num só sítio.
 *
 * É o sítio onde se aprovam «aos magotes» os vídeos filmados no ginásio: selecionam-se vários, escreve-se
 * uma nota do que se verificou e decide-se de uma vez. A mesma nota cobre o bloco; quem decide e quando
 * fica registado em cada ficheiro (é o servidor que o guarda, com o nome de quem está autenticado).
 * Nada aqui é um atalho que contorne a regra: aprovar vídeo continua a exigir dizer o que se verificou,
 * rejeitar continua a exigir o motivo, e o que veio do catálogo é recusado item a item.
 *
 * Props:
 *   meta          o /meta da biblioteca (canManage)
 *   onCountChange (totalPendentes) => void   — para o número no separador
 *   onDecided     () => void                 — avisar o resto da página (contadores, lista)
 */

const PAGE_SIZE = 24;
const STATUS_LABEL = { pending: 'Por validar', rejected: 'Rejeitados' };

const keyOf = (item) => `${item.exerciseId}:${item.media.id}`;

export default function ValidationPanel({ meta, onCountChange, onDecided }) {
  const [status, setStatus] = useState('pending');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ items: [], total: 0, page: 1, pageSize: PAGE_SIZE });
  const [selected, setSelected] = useState(() => new Set());
  const [note, setNote] = useState('');
  const [confirm, setConfirm] = useState(null);   // 'approve' | 'reject'
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    exerciseLibrary.mediaQueue({ status, page, pageSize: PAGE_SIZE })
      .then(({ data }) => {
        if (!alive) return;
        setResult(data);
        setError(null);
        setSelected(new Set());
        if (status === 'pending') onCountChange?.(data.total);
      })
      .catch((e) => { if (alive) setError(errorList(e, 'Não foi possível carregar a fila de validação.').join(' ')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page, reloadKey]);

  const pages = Math.max(1, Math.ceil(result.total / result.pageSize));
  const chosen = useMemo(() => result.items.filter((i) => selected.has(keyOf(i))), [result.items, selected]);
  // Aprovar exige nota para o que se TEM de ver: um vídeo (nosso) e um vídeo incorporado (que se abre no YouTube).
  const described = chosen.filter((i) => i.media.kind === 'video' || i.media.kind === 'embed').length;
  const videos = chosen.filter((i) => i.media.kind === 'video').length;

  const toggle = (item) => setSelected((prev) => {
    const next = new Set(prev);
    const k = keyOf(item);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const allChosen = result.items.length > 0 && chosen.length === result.items.length;
  const toggleAll = () => setSelected(allChosen ? new Set() : new Set(result.items.map(keyOf)));

  // As mesmas regras do servidor, ditas antes de tentar: aprovar vídeo pede o que se verificou, rejeitar pede o motivo.
  const noteProblem = (decision) => {
    if (decision === 'reject') return note.trim().length < 3 ? 'Escreva o motivo da rejeição.' : null;
    if (decision === 'approve' && described > 0 && note.trim().length < 10) return 'Para aprovar vídeo, descreva o que verificou (movimento correto, enquadramento, sem som, ciclo limpo).';
    return null;
  };
  const canDecide = (decision) => chosen.length > 0 && !busy && !noteProblem(decision);

  const confirmMessage = (decision) => {
    const n = chosen.length;
    const many = `${n} ${n === 1 ? 'ficheiro' : 'ficheiros'}`;
    const which = videos > 0 ? ` (${videos} ${videos === 1 ? 'vídeo' : 'vídeos'})` : '';
    return decision === 'approve'
      ? `Vai aprovar ${many}${which}. Passam a aparecer aos atletas. Fica registado o seu nome e a data.`
      : `Vai rejeitar ${many}${which}. Deixam de ser mostrados aos atletas.`;
  };

  const decide = async () => {
    const decision = confirm;
    const items = chosen.map((i) => ({ exerciseId: i.exerciseId, mediaId: i.media.id }));
    setBusy(true);
    try {
      const { data } = await exerciseLibrary.reviewMediaBatch(decision, note.trim(), items);
      setReport(data);
      const parts = [];
      if (data.approved) parts.push(`${data.approved} aprovados`);
      if (data.rejected) parts.push(`${data.rejected} rejeitados`);
      if (data.pending) parts.push(`${data.pending} de volta a pendente`);
      if (data.failed) parts.push(`${data.failed} sem efeito`);
      const summary = parts.join(', ') || 'Sem alterações';
      if (data.failed) toast.error(summary); else toast.success(summary);
      setNote('');
      setSelected(new Set());
      setConfirm(null);
      setReloadKey((k) => k + 1);
      onDecided?.();
    } catch (e) {
      setError(errorList(e, 'Não foi possível guardar a decisão.').join(' '));
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="lib-filters">
        <div className="lib-filters-top">
          <div style={{ display: 'flex', gap: 8 }} role="group" aria-label="Estado a validar">
            {['pending', 'rejected'].map((s) => (
              <button key={s} type="button" className={`lib-btn small${status === s ? ' primary' : ''}`}
                aria-pressed={status === s} onClick={() => { setStatus(s); setPage(1); setReport(null); }}>
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
          <button type="button" className="lib-btn small" disabled={result.items.length === 0 || busy} onClick={toggleAll}>
            {allChosen ? 'Não selecionar nenhum' : 'Selecionar tudo nesta página'}
          </button>
        </div>
      </div>

      {error && <div className="lib-notice bad" role="alert">{error}</div>}

      {report && (
        <div className={`lib-notice${report.failed ? ' warn' : ''}`} role="status" data-testid="validation-report">
          Decisão registada: {report.approved} aprovados, {report.rejected} rejeitados, {report.pending} a pendente
          {report.failed > 0 && <>, {report.failed} sem efeito</>}.
          {report.failed > 0 && (
            <ul style={{ margin: '6px 0 0 18px' }}>
              {report.rows.filter((r) => r.action === 'failed').map((r) => (
                <li key={`${r.exerciseId}:${r.mediaId}`}>{r.exerciseName || `Exercício ${r.exerciseId}`}: {r.reason}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {loading && result.items.length === 0 && <div className="lib-loading" role="status">A carregar…</div>}
      {!loading && !error && result.items.length === 0 && (
        <div className="lib-empty">
          {status === 'pending' ? 'Não há nada à espera de validação. Está tudo em dia.' : 'Não há ficheiros rejeitados.'}
        </div>
      )}

      <div className="lib-worklist" data-testid="validation-queue">
        {result.items.map((item) => {
          const k = keyOf(item);
          const isChosen = selected.has(k);
          const media = item.media;
          return (
            <div key={k} className={`lib-media-item${isChosen ? ' selected' : ''}`} data-testid={`queue-${k}`}>
              <label className="lib-check" style={{ minHeight: 44 }}>
                <input type="checkbox" checked={isChosen} disabled={busy} onChange={() => toggle(item)}
                  aria-label={`Selecionar ${item.exerciseName}${media.kind === 'video' ? ', vídeo' : media.kind === 'embed' ? ', vídeo do YouTube' : ''}`} />
                <span className="lib-card-title">{item.exerciseName}</span>
              </label>
              {item.groupLabel && (
                <span className="lib-chips">
                  <Chip tone="accent">{item.groupLabel}</Chip>
                  <Chip>{media.kind === 'video' ? 'Vídeo' : media.kind === 'embed' ? 'Vídeo do YouTube' : 'Imagem'}</Chip>
                </span>
              )}
              {media.kind === 'video'
                ? <video src={media.url} controls muted loop playsInline preload="metadata" aria-label={`Demonstração de ${item.exerciseName}`} />
                : media.kind === 'embed'
                  // O vídeo incorporado vê-se aqui antes de se aprovar: o player oficial, no próprio ecrã da fila.
                  ? (
                    <div className="lib-embed-frame">
                      <iframe src={media.url} title={media.altText ? `Vídeo do YouTube: ${media.altText}` : `Vídeo do YouTube de ${item.exerciseName}`}
                        loading="lazy" referrerPolicy="strict-origin-when-cross-origin"
                        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen" />
                      <span className="lib-hint">
                        {media.channelName ? `Canal: ${media.channelName}. ` : ''}
                        {media.sourceUrl ? <a href={media.sourceUrl} target="_blank" rel="noopener noreferrer">Abrir no YouTube</a> : 'Conteúdo de terceiros, incorporado.'}
                      </span>
                    </div>
                  )
                  : <img src={media.thumbUrl || media.url} alt={media.altText || `Ficheiro de ${item.exerciseName}`} loading="lazy" decoding="async" />}
              {media.validationStatus !== 'approved' && (
                <span className="lib-hint" data-testid={`queue-status-${k}`}>
                  {media.validationStatus === 'rejected' ? 'Rejeitado. ' : 'Os atletas ainda não o veem: falta a validação de uma pessoa. '}
                  {media.reviewNote || ''}
                </span>
              )}
              {(media.license || media.sourceUrl || media.sizeBytes) && (
                <details className="lib-hint">
                  <summary>Origem e tamanho</summary>
                  {media.license && <div>Licença: {media.license}</div>}
                  {media.sourceUrl && <div>Origem: <a href={media.sourceUrl} target="_blank" rel="noopener noreferrer">{media.sourceUrl}</a></div>}
                  {media.sizeBytes ? <div>Tamanho: {(media.sizeBytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB</div> : null}
                </details>
              )}
            </div>
          );
        })}
      </div>

      {result.total > 0 && (
        <div className="lib-pager">
          <span>{result.total} {result.total === 1 ? 'ficheiro' : 'ficheiros'} · página {result.page} de {pages}</span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="lib-btn small" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Anterior</button>
            <button type="button" className="lib-btn small" disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)}>Seguinte</button>
          </span>
        </div>
      )}

      {meta.canManage && result.items.length > 0 && (
        <div className="lib-field" style={{ padding: 10, border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', marginTop: 12 }}>
          <label className="lib-label" htmlFor="validation-note">Nota da decisão (obrigatória para aprovar vídeo e para rejeitar)</label>
          <textarea id="validation-note" className="lib-input lib-textarea" rows={2} maxLength={500} value={note}
            placeholder="O que verificou: movimento correto, enquadramento de lado, sem som, ciclo limpo…"
            onChange={(e) => setNote(e.target.value)} disabled={busy} />
          <span className="lib-hint" data-testid="validation-note-hint">
            {chosen.length === 0
              ? 'Selecione os ficheiros que quer decidir.'
              : noteProblem('approve') || (described > 0 ? `${described} ${described === 1 ? 'vídeo' : 'vídeos'} na seleção: a nota acima cobre todos.` : 'A nota é opcional para imagens.')}
          </span>
          <div className="lib-media-actions" style={{ marginTop: 10 }}>
            <button type="button" className="lib-btn primary" disabled={!canDecide('approve')}
              onClick={() => setConfirm('approve')} data-testid="batch-approve">
              Aprovar selecionados{chosen.length > 0 ? ` (${chosen.length})` : ''}
            </button>
            <button type="button" className="lib-btn danger" disabled={!canDecide('reject')}
              onClick={() => setConfirm('reject')} data-testid="batch-reject">
              Rejeitar selecionados{chosen.length > 0 ? ` (${chosen.length})` : ''}
            </button>
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm === 'approve' ? 'Aprovar em bloco' : 'Rejeitar em bloco'}
          message={confirmMessage(confirm)}
          confirmLabel={confirm === 'approve' ? 'Aprovar' : 'Rejeitar'}
          danger={confirm === 'reject'}
          busy={busy}
          onConfirm={decide}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
